import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_CANCEL_GENERATE_AI_POST, API_KEY } from '../../utils/constants';
import { useSelector } from 'react-redux';
import { authSelector } from '../../store/slices/auth';

export default function Countdown({
  selectedClient,
  generationStartTime,
  onComplete
}) {
  const { userInfo } = useSelector(authSelector);

  // Use the passed start timestamp as prop, or current as fallback
  const generationStartedAtMs = generationStartTime || Date.now();
  const targetTime = generationStartedAtMs + 9 * 60 * 1000;

  const [timeLeft, setTimeLeft] = useState(targetTime - Date.now());
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = targetTime - Date.now();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        // After 9 minutes, check if generatingContentByAI is false
        if (selectedClient?.generatingContentByAI === false) {
          setIsComplete(true);
          clearInterval(interval);
        }
        // If still true, keep showing counter at 00:00
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime, selectedClient?.generatingContentByAI]);

  // Function to format remaining time
  const formatTime = (ms) => {
    if (ms <= 0) {
      return '00:00';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Function to reload the page
  const handleReload = () => {
    if (onComplete) {
      onComplete();
    }
    window.location.reload();
  };

  // Function to show cancel confirmation
  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  // Function to cancel the AI generation after confirmation
  const handleCancelConfirm = async () => {
    if (isCancelling) return; // Prevent multiple clicks

    setIsCancelling(true);
    setShowCancelConfirm(false);

    try {
      const response = await axios.post(
        API_CANCEL_GENERATE_AI_POST,
        { clientId: selectedClient.id },
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Call onComplete to close the modal and reset state
        if (onComplete) {
          onComplete();
        }
        // Reload the page after successful cancellation
        window.location.reload();
      }
    } catch (error) {
      console.error('Error cancelling AI generation:', error);
      // Still call onComplete to close the modal even if the API call fails
      if (onComplete) {
        onComplete();
      }
    } finally {
      setIsCancelling(false);
    }
  };

  // Function to cancel the cancellation
  const handleCancelDismiss = () => {
    setShowCancelConfirm(false);
  };

  return (
    <div
      className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        top: 0,
        left: 0
      }}>
      <div
        className="bg-white shadow-lg"
        style={{
          maxWidth: '400px',
          width: '90%',
          borderRadius: '12px',
          padding: '32px',
          border: 'none',
          fontFamily: "'IBM Plex Sans', sans-serif",
          opacity: showCancelConfirm ? 1 : 1,
          transform: showCancelConfirm ? 'scale(1)' : 'scale(1)',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
        }}>
        {!isComplete ? (
          <>
            {!showCancelConfirm ? (
              <>
                <div className="text-center mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: '64px',
                      height: '64px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '50%',
                      border: '3px solid #3766a8'
                    }}>
                    <i
                      className="fas fa-cog fa-spin"
                      style={{ fontSize: '20px', color: '#3766a8' }}></i>
                  </div>
                  <h5
                    className="mb-2 text-dark"
                    style={{
                      fontWeight: '600',
                      fontSize: '18px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    Generating Content
                  </h5>
                  <p
                    className="text-muted mb-3"
                    style={{
                      fontSize: '14px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    AI is creating posts for your client
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div
                    className="mb-1"
                    style={{
                      color: '#3766a8',
                      fontSize: '28px',
                      fontWeight: '700',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    {formatTime(timeLeft)}
                  </div>
                  <div
                    className="text-muted"
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                    remaining
                  </div>
                </div>

                <div
                  className="progress mb-4"
                  style={{
                    height: '6px',
                    borderRadius: '3px',
                    backgroundColor: '#f8f9fa'
                  }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      backgroundColor: '#3766a8',
                      width: `${Math.max(
                        0,
                        Math.min(
                          100,
                          ((9 * 60 * 1000 - timeLeft) / (9 * 60 * 1000)) * 100
                        )
                      )}%`,
                      borderRadius: '3px',
                      transition: 'width 0.3s ease'
                    }}></div>
                </div>

                <div className="text-center mb-4">
                  <button
                    className="btn"
                    onClick={handleCancelClick}
                    disabled={isCancelling}
                    style={{
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '800',
                      backgroundColor: '#AAAAAA',
                      border: '2px solid #AAAAAA',
                      color: '#fff',
                      padding: '7px 16px',
                      minWidth: '100px',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCancelling) {
                        e.target.style.backgroundColor = '#999999';
                        e.target.style.borderColor = '#999999';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCancelling) {
                        e.target.style.backgroundColor = '#AAAAAA';
                        e.target.style.borderColor = '#AAAAAA';
                      }
                    }}>
                    {isCancelling ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-1"></i>
                        Cancelling...
                      </>
                    ) : (
                      'Cancel'
                    )}
                  </button>
                </div>

                <div className="text-center">
                  <small
                    className="text-muted"
                    style={{
                      fontSize: '12px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    <i className="fas fa-info-circle me-1"></i>
                    You can navigate to other sections
                  </small>
                </div>
              </>
            ) : (
              <>
                {/* Confirmation State */}
                <div className="text-center mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: '64px',
                      height: '64px',
                      backgroundColor: '#fff3cd',
                      borderRadius: '50%',
                      border: '3px solid #ffc107'
                    }}>
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ fontSize: '20px', color: '#ffc107' }}></i>
                  </div>
                  <h5
                    className="mb-2 text-dark"
                    style={{
                      fontWeight: '600',
                      fontSize: '18px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    Are you sure?
                  </h5>
                  <p
                    className="text-muted mb-3"
                    style={{
                      fontSize: '14px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    This action will cancel the ongoing content generation. You
                    won't be able to undo this action.
                  </p>
                </div>

                <div className="d-flex gap-3 mb-4">
                  <button
                    className="btn flex-fill"
                    onClick={handleCancelDismiss}
                    style={{
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '800',
                      backgroundColor: '#3766a8',
                      border: '2px solid #3766a8',
                      color: '#fff',
                      padding: '7px 12px',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#2c4a7a';
                      e.target.style.borderColor = '#2c4a7a';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#3766a8';
                      e.target.style.borderColor = '#3766a8';
                    }}>
                    No, continue
                  </button>
                  <button
                    className="btn flex-fill"
                    onClick={handleCancelConfirm}
                    disabled={isCancelling}
                    style={{
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '800',
                      backgroundColor: '#AAAAAA',
                      border: '2px solid #AAAAAA',
                      color: '#fff',
                      padding: '7px 12px',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCancelling) {
                        e.target.style.backgroundColor = '#999999';
                        e.target.style.borderColor = '#999999';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCancelling) {
                        e.target.style.backgroundColor = '#AAAAAA';
                        e.target.style.borderColor = '#AAAAAA';
                      }
                    }}>
                    {isCancelling ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-1"></i>
                        Cancelling...
                      </>
                    ) : (
                      'Yes, cancel'
                    )}
                  </button>
                </div>

                <div className="text-center">
                  <small
                    className="text-muted"
                    style={{
                      fontSize: '12px',
                      fontFamily: "'IBM Plex Sans', sans-serif"
                    }}>
                    <i className="fas fa-info-circle me-1"></i>
                    You can navigate to other sections
                  </small>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#d4edda',
                  borderRadius: '50%',
                  border: '3px solid #28a745'
                }}>
                <i
                  className="fas fa-check"
                  style={{ fontSize: '20px', color: '#28a745' }}></i>
              </div>
              <h5
                className="mb-2 text-success"
                style={{
                  fontWeight: '600',
                  fontSize: '18px',
                  fontFamily: "'IBM Plex Sans', sans-serif"
                }}>
                Content Ready!
              </h5>
              <p
                className="text-muted mb-3"
                style={{
                  fontSize: '14px',
                  fontFamily: "'IBM Plex Sans', sans-serif"
                }}>
                Your AI-generated content is complete
              </p>
            </div>

            <button
              className="btn w-100"
              onClick={handleReload}
              style={{
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '800',
                backgroundColor: '#3766a8',
                border: '2px solid #3766a8',
                color: '#fff',
                padding: '7px 16px',
                fontFamily: "'IBM Plex Sans', sans-serif",
                letterSpacing: '1px',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2c4a7a';
                e.target.style.borderColor = '#2c4a7a';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3766a8';
                e.target.style.borderColor = '#3766a8';
              }}>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Calendar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
